import { useState, useRef, useEffect, useId } from 'react';
import { Tooltip } from 'components/Widget/Tooltip';
import * as S from './styles';

export interface SwatchPickerOption {
	id: string;
	color: string;
	label: string;
}

interface SwatchPickerProps {
	options: SwatchPickerOption[];
	activeId?: string | null;
	defaultId?: string | null;
	triggerColor: string;
	onSelect: (id: string) => void;
	tooltip?: string;
	disabled?: boolean;
}

const SwatchPicker = ({
	options,
	activeId,
	defaultId,
	triggerColor,
	onSelect,
	tooltip = 'Preview background',
	disabled = false,
}: SwatchPickerProps) => {
	const tooltipId = useId();
	const [isOpen, setIsOpen] = useState(false);
	const containerRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		const onMouseDown = (e: MouseEvent) => {
			if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
				setIsOpen(false);
			}
		};

		if (isOpen) {
			document.addEventListener('mousedown', onMouseDown);
			return () => document.removeEventListener('mousedown', onMouseDown);
		}
	}, [isOpen]);

	const onOptionClick = (id: string) => {
		onSelect(id);
		setIsOpen(false);
	};

	const effectiveActiveId = activeId ?? defaultId ?? null;

	const onSwatchDoubleClick = () => {
		if (disabled || options.length !== 2) return;
		const other = options.find((o) => o.id !== effectiveActiveId);
		if (other) onSelect(other.id);
		setIsOpen(false);
	};

	const getDropdownPosition = () => {
		const rect = containerRef.current?.getBoundingClientRect();
		if (!rect) return { top: 0, right: 0 };
		return {
			top: rect.bottom + 4,
			right: window.innerWidth - rect.right,
		};
	};

	// Only compute position when dropdown is open
	const dropdownPosition = isOpen ? getDropdownPosition() : { top: 0, right: 0 };

	return (
		<S.Base ref={containerRef}>
			<S.Swatch
				$color={triggerColor}
				$disabled={disabled}
				onClick={() => !disabled && setIsOpen((prev) => !prev)}
				onDoubleClick={onSwatchDoubleClick}
				data-tooltip-id={tooltip ? tooltipId : undefined}
				data-tooltip-content={tooltip}
			/>
			{tooltip && !isOpen && <Tooltip id={tooltipId} placement="bottom" />}
			{isOpen && (
				<S.Dropdown $top={dropdownPosition.top} $right={dropdownPosition.right}>
					{options.map((opt) => (
						<S.Option
							key={opt.id}
							$active={opt.id === effectiveActiveId}
							onClick={() => onOptionClick(opt.id)}
						>
							<S.OptionSwatch $color={opt.color} />
							{opt.label}
						</S.Option>
					))}
				</S.Dropdown>
			)}
		</S.Base>
	);
};

export default SwatchPicker;
